import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { enableProdMode, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { getAnalytics, provideAnalytics, ScreenTrackingService, UserTrackingService } from '@angular/fire/analytics';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { connectDatabaseEmulator, getDatabase, provideDatabase } from '@angular/fire/database';
import { connectFirestoreEmulator, getFirestore, provideFirestore } from '@angular/fire/firestore';

import { connectStorageEmulator, getStorage, provideStorage } from '@angular/fire/storage';
import { MatChipsModule } from '@angular/material/chips';
import { MAT_RIPPLE_GLOBAL_OPTIONS, provideNativeDateAdapter, RippleGlobalOptions } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, Route, withComponentInputBinding, withEnabledBlockingInitialNavigation } from '@angular/router';
import { APP_ACCOUNT_CONFIG } from '@ss/shared/account-settings/config';
import { mainGuard } from '@ss/shared/account-settings/nta-guards';
import { providePccAuth } from '@ss/shared/auth';
import { PccTokenInterceptor, TokenInterceptor } from '@ss/shared/firebase-utils';
import { firebaseConfig, pccConfig } from '@ss/typings/config';
import { NG_EVENT_PLUGINS } from '@tinkoff/ng-event-plugins';
import { provideMarkdown } from 'ngx-markdown';
import { AppComponent } from './app/app.component';
import { environment } from './environments/environment';


const globalRippleConfig: RippleGlobalOptions = {
	disabled: true,
	animation: {
		enterDuration: 0,
		exitDuration: 0,
	},
};
const routes: Route[] = [
	{
		path: 'account',
		loadChildren: () => import('@ss/nta-hunter/account').then((m) => m.ACCOUNT_ROUTES),
	},
	{
		path: '',
		loadChildren: () => import('@ss/nta-hunter/core').then((m) => m.CORE_ROUTES),
		canActivate: [mainGuard]
	},
	{ path: '**', redirectTo: 'enterprise' },
];

if (environment.production) {
	enableProdMode();
}

const FIRESTORE_PORT = 5002;
const DATABASE_PORT = 9000;
const STORAGE_PORT = 9199;

bootstrapApplication(AppComponent, {
	providers: [
		provideZoneChangeDetection({ eventCoalescing: true }),
		provideFirebaseApp(() => initializeApp(firebaseConfig)),
		provideAnalytics(() => getAnalytics()),
		provideAuth(() => getAuth()),
		provideFirestore(() => {
			const firestore = getFirestore();
			if (firebaseConfig.useEmulator) connectFirestoreEmulator(firestore, 'localhost', FIRESTORE_PORT);
			return firestore;
		}),
		provideStorage(() => {
			const storage = getStorage();
			if (firebaseConfig.useEmulator) connectStorageEmulator(storage, 'localhost', STORAGE_PORT);
			return storage;
		}),
		provideDatabase(() => {
			const database = getDatabase();
			if (firebaseConfig.useEmulator) connectDatabaseEmulator(database, 'localhost', DATABASE_PORT);
			return database;
		}),
		importProvidersFrom(BrowserModule, MatChipsModule, MatIconModule),
		providePccAuth({
			clientId: pccConfig.clientId,
			redirectUri: pccConfig.redirectUri,
			scopes: ['openid', 'profile'],
			preserveRoute: true,
			quickStart: false,
			checkExpiring: false,
		}),
		{ provide: APP_ACCOUNT_CONFIG, useValue: { smallLogoUrl: './assets/nta_favicon.svg', mainLogoUrl: './assets/Simpli360.svg', appName: 'Simpli360' } },
		{ provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: globalRippleConfig },
		{ provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: PccTokenInterceptor, multi: true },
		MatIconRegistry,
		MatSnackBar,
		provideAnimations(),
		provideHttpClient(withXhr(), withInterceptorsFromDi()),
		provideRouter(
			routes,
			withEnabledBlockingInitialNavigation(),
			withComponentInputBinding()
		),
		provideMarkdown(),
		importProvidersFrom(MatDialogModule),
		ScreenTrackingService,
		UserTrackingService,
		NG_EVENT_PLUGINS,
		provideNativeDateAdapter()
	]
})
	.catch((err) => console.error(err));
